import { SectionTitle } from '@/components/shared/Content';
import horizontalForkedArrow from '@/static/claims/forked-arrow.png';
import horizontalArrow from '@/static/claims/horizontal-arrow.png';
import verticalArrow from '@/static/claims/vertical-arrow.png';
import verticalForkedArrow from '@/static/claims/vertical-forked-arrow.png';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import styled, { css } from 'styled-components';

const HowItWorksSectionInner = styled(Flex)<{ background: string }>`
  max-width: 1100px;
  background: ${({ background }) => background};

  ${MEDIA_QUERIES.desktop`
        margin: 0 auto;
    `}
`;

const HowItWorksSubtitle = styled.h3`
  padding-top: 20px;
  text-align: center;

  ${font('main', { fontSize: '2rem', lineHeight: '3rem' })}

  ${MEDIA_QUERIES.desktop`
        white-space: pre-line;
    `}
`;

const forkedArrowStyles = css`
  ${({ forkedArrow }: { forkedArrow: boolean }) => css`
    background: transparent url(${forkedArrow ? verticalForkedArrow.src : verticalArrow.src}) no-repeat;
    background-size: contain;

    ${MEDIA_QUERIES.desktop`
        background: transparent url(${forkedArrow ? horizontalForkedArrow.src : horizontalArrow.src}) no-repeat;
        background-size: contain;
    `}
  `}
`;

const InstantOrHuman = styled(Flex)`
  ${MEDIA_QUERIES.desktop`
        &:first-child {
            min-height: 99px;
        }
    `}
`;

const HowItWorksStep = styled(Flex)`
  align-items: ${props => props.alignItems || 'center'};
  position: relative;

  ${props => props.height && `height: ${props.height};`}

  &:after {
    content: '';
    position: absolute;
    top: ${({ arrowTop }) => arrowTop?.[0]};
    left: ${({ arrowLeft }) => arrowLeft?.[0]};
    width: ${({ arrowWidth }) => arrowWidth?.[0]};
    height: ${({ arrowHeight }) => arrowHeight?.[0]};

    ${MEDIA_QUERIES.tablet`
            top: ${({ arrowTop }) => arrowTop?.[1]};
        `}

    ${MEDIA_QUERIES.desktop`
            width: ${({ arrowWidth }) => arrowWidth?.[1]};
            height: ${({ arrowHeight }) => arrowHeight?.[1]};
            top: ${({ arrowTop }) => arrowTop?.[2]};
            left: ${({ arrowLeft }) => arrowLeft?.[1]};
    `}

        ${forkedArrowStyles}
  }
`;

const HowItWorksStepParagraph = styled.p`
  margin-top: 16px;
  text-align: center;
  ${font('main', { fontSize: '2rem', lineHeight: '2.6rem', fontWeight: 400 })};

  ${MEDIA_QUERIES.tabletAndDesktop`
        width: 100%;
    `}
`;

const InstantOrHumanParagraph = styled(Box)`
  ${font('main', { fontSize: '2rem', lineHeight: '2.6rem', fontWeight: 400 })};
  text-align: center;

  ${MEDIA_QUERIES.desktop`
        text-align: left;
    `}
`;

const HowItWorksSection = styled(Flex)<{ darkBackground: boolean }>`
  background: ${({ darkBackground }) => (darkBackground ? themedColor('cloud') : themedColor('white'))};
`;

const CenterImage = styled(Image)`
  margin-inline: auto;
`;

interface Step {
  arrowTop?: [string, string, string];
  image: StaticImageData;
  content: string;
}

type StepName = 'claim_button' | 'what_happened' | 'anti_fraud' | 'instantly_approved' | 'humans';

export interface HowItWorksProps {
  title: string;
  subtitle: string;
  steps: Record<StepName, Step>;
  darkBackground?: boolean;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ title, subtitle, steps, darkBackground }) => {
  return (
    <HowItWorksSection darkBackground={darkBackground}>
      <HowItWorksSectionInner flexDirection="column" width={1} pt={[80, 100]} pb={[90, 50]} px={[20, 0]}>
        <SectionTitle tabIndex={0}>{title}</SectionTitle>
        <HowItWorksSubtitle tabIndex={0}>{subtitle}</HowItWorksSubtitle>
        <Flex
          width={[1]}
          alignItems={['center', 'center', 'flex-start']}
          flexDirection={['column', 'column', 'row']}
          mt={[50, 80]}
        >
          <HowItWorksStep
            flexDirection="column"
            width={['auto', 270]}
            height="219px"
            arrowWidth={['17px', '32px']}
            arrowHeight={['29px', '18px']}
            arrowTop={steps.claim_button.arrowTop}
            arrowLeft={['auto', '223px']}
            mb={[67]}
            tabIndex={0}
          >
            <Image
              aria-hidden="true"
              width={142}
              height={142}
              src={steps.claim_button.image}
              alt={steps.claim_button.content}
            />
            <HowItWorksStepParagraph>{parse(steps.claim_button.content)}</HowItWorksStepParagraph>
          </HowItWorksStep>
          <HowItWorksStep
            flexDirection="column"
            width={['auto', 157]}
            arrowWidth={['17px', '32px']}
            arrowHeight={['29px', '18px']}
            arrowTop={steps.what_happened.arrowTop}
            arrowLeft={['auto', '171px']}
            mb={[67]}
            ml={[0, 0, 41]}
            tabIndex={0}
          >
            <Image
              aria-hidden="true"
              width={158}
              height={142}
              src={steps.what_happened.image}
              alt={steps.what_happened.content}
            />
            <HowItWorksStepParagraph>{steps.what_happened.content}</HowItWorksStepParagraph>
          </HowItWorksStep>
          <HowItWorksStep
            flexDirection="column"
            width={['auto', 240]}
            arrowWidth={['185px', '110px']}
            arrowHeight={['57px', '136px']}
            arrowTop={steps.anti_fraud.arrowTop}
            arrowLeft={['auto', '190px']}
            forkedArrow
            mb={[85, 85, 0]}
            ml={[0, 0, 22]}
            tabIndex={0}
          >
            <Image
              aria-hidden="true"
              width={142}
              height={142}
              src={steps.anti_fraud.image}
              alt={steps.anti_fraud.content}
            />
            <HowItWorksStepParagraph>{steps.anti_fraud.content}</HowItWorksStepParagraph>
          </HowItWorksStep>
          <HowItWorksStep
            width={['auto', 326]}
            alignItems="flex-start"
            flexDirection={['row', 'row', 'column']}
            mt={[14, 14, '-7px']}
            ml={[0, 0, 95]}
            tabIndex={0}
          >
            <InstantOrHuman width={['auto', 326]} flexDirection={['column', 'column', 'row']}>
              <CenterImage
                aria-hidden="true"
                width={52}
                height={52}
                src={steps.instantly_approved.image}
                alt={steps.instantly_approved.content}
              />
              <InstantOrHumanParagraph as="p" mt={[10, 10, '-7px']} ml={['inherit', 'inherit', 26]}>
                {parse(steps.instantly_approved.content)}
              </InstantOrHumanParagraph>
            </InstantOrHuman>
            <InstantOrHuman
              width={['auto', 326]}
              flexDirection={['column', 'column', 'row']}
              ml={[10, 10, 0]}
              mt={[0, 0, 26]}
            >
              <CenterImage
                aria-hidden="true"
                width={52}
                height={52}
                src={steps.humans.image}
                alt={steps.humans.content}
              />
              <InstantOrHumanParagraph as="p" mt={[10, 10, '-7px']} ml={['inherit', 'inherit', 26]} px={[10, 10, 0]}>
                {parse(steps.humans.content)}
              </InstantOrHumanParagraph>
            </InstantOrHuman>
          </HowItWorksStep>
        </Flex>
      </HowItWorksSectionInner>
    </HowItWorksSection>
  );
};

export default HowItWorks;
